import { CHUNK_SIZE } from '../terrain/TerrainConstants.js';
import { WATER_LEVEL } from '../terrain/TerrainConstants.js'; 

export class TerrainCollisionManager {
    static instance = null;

    static getInstance() {
        if (!TerrainCollisionManager.instance) {
            TerrainCollisionManager.instance = new TerrainCollisionManager();
        }
        return TerrainCollisionManager.instance;
    }

    constructor() {
        // Prevent direct instantiation
        if (TerrainCollisionManager.instance) {
            return TerrainCollisionManager.instance;
        }

        this.collisionMaps = new Map(); // Stores height maps by chunk key
        this.debug = false;
        this.safetyFallbackHeight = WATER_LEVEL + 1; // Safe fallback height
        this._lastDebugTime = 0; // Add timestamp for rate limiting
        
        // Add collision check points relative to player center
        this.collisionCheckPoints = [
            {x: 0, z: 0},      // Center
            {x: 0.4, z: 0},    // Right
            {x: -0.4, z: 0},   // Left
            {x: 0, z: 0.4},    // Front
            {x: 0, z: -0.4},   // Back
            {x: 0.3, z: 0.3},  // Front-Right
            {x: -0.3, z: 0.3}, // Front-Left
            {x: 0.3, z: -0.3}, // Back-Right
            {x: -0.3, z: -0.3} // Back-Left
        ];
        
        // Set the instance
        TerrainCollisionManager.instance = this;
    }

    // Clear all collision data for a vertical column
    clearBlockColumn(worldX, worldZ) {
        try {
            // Convert world coordinates to chunk coordinates
            const chunkX = Math.floor(worldX / CHUNK_SIZE);
            const chunkZ = Math.floor(worldZ / CHUNK_SIZE);
            
            // Get local coordinates within chunk
            const localX = ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localZ = ((worldZ % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            
            // Get or create the collision map for this chunk
            const key = `${chunkX},${chunkZ}`;
            let collisionMap = this.collisionMaps.get(key);
            
            if (!collisionMap) {
                collisionMap = new Array(CHUNK_SIZE).fill(0)
                    .map(() => new Array(CHUNK_SIZE).fill(this.safetyFallbackHeight));
                this.collisionMaps.set(key, collisionMap);
            }
            
            // Reset the height to a very low value to effectively clear collision
            if (localX >= 0 && localX < CHUNK_SIZE && localZ >= 0 && localZ < CHUNK_SIZE) {
                collisionMap[localX][localZ] = -999; // Use a very low value to ensure no collision
            }
        } catch (error) {
            console.error(`Error clearing block column at (${worldX}, ${worldZ}):`, error);
        }
    }

    // Initialize a collision map for a specific chunk
    initializeChunkCollision(chunkX, chunkZ) {
        const key = `${chunkX},${chunkZ}`;
        if (!this.collisionMaps.has(key)) {
            // Create empty height map with safe fallback heights
            const collisionMap = new Array(CHUNK_SIZE).fill(0)
                .map(() => new Array(CHUNK_SIZE).fill(this.safetyFallbackHeight));
            this.collisionMaps.set(key, collisionMap);
        }
        return this.collisionMaps.get(key);
    }

    // Set height value for a specific block with proper error handling
    setBlockHeight(worldX, worldZ, height) {
        try {
            // Handle undefined or NaN inputs
            if (height === undefined || isNaN(height)) {
                console.warn(`Invalid height at (${worldX}, ${worldZ}): ${height}, using fallback value`);
                height = this.safetyFallbackHeight;
            }

            // Convert world coordinates to chunk coordinates
            const chunkX = Math.floor(worldX / CHUNK_SIZE);
            const chunkZ = Math.floor(worldZ / CHUNK_SIZE);
            
            // Get local coordinates within chunk with proper modulo handling
            const localX = ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localZ = ((worldZ % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            
            // Ensure the map exists
            const collisionMap = this.initializeChunkCollision(chunkX, chunkZ);
            
            // Set the precise height for this block
            if (localX >= 0 && localX < CHUNK_SIZE && localZ >= 0 && localZ < CHUNK_SIZE) {
                collisionMap[localX][localZ] = height;
            }
        } catch (error) {
            console.error(`Error setting block height at (${worldX}, ${worldZ}):`, error);
        }
    }

    // Get precise height for any world position with robust error handling
    getCollisionHeight(worldX, worldZ) {
        try {
            // Round to nearest integer for proper block alignment
            const x = Math.floor(worldX);
            const z = Math.floor(worldZ);
            
            // Convert world coordinates to chunk coordinates
            const chunkX = Math.floor(x / CHUNK_SIZE);
            const chunkZ = Math.floor(z / CHUNK_SIZE);
            
            // Get local coordinates within chunk with proper modulo handling
            const localX = ((x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            const localZ = ((z % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
            
            // Get the chunk's collision map
            const key = `${chunkX},${chunkZ}`;
            const collisionMap = this.collisionMaps.get(key);
            
            // If no collision data exists yet, fall back to terrain manager
            if (!collisionMap) {
                return window.chunkManager ? 
                    window.chunkManager.getHeightAt(x, z) : 
                    this.safetyFallbackHeight;
            }
            
            // Return the exact block height
            if (localX >= 0 && localX < CHUNK_SIZE && localZ >= 0 && localZ < CHUNK_SIZE) {
                const height = collisionMap[localX][localZ];
                
                // Additional safety check for invalid heights
                if (height === undefined || isNaN(height)) {
                    return this.safetyFallbackHeight;
                }
                
                return height;
            } else {
                return this.safetyFallbackHeight;
            }
        } catch (error) {
            console.error(`Error getting collision height at (${worldX}, ${worldZ}):`, error);
            return this.safetyFallbackHeight;
        }
    }
    
    // Check if there's a block at a specific height that would block the player's head
    checkHeadCollision(worldX, worldZ, playerHeadHeight) {
        const terrainHeight = this.getCollisionHeight(worldX, worldZ);
        return terrainHeight > (playerHeadHeight - 2.0);
    }
    
    // Add new method for strict collision checking
    checkStrictCollision(playerX, playerY, playerZ, radius = 0.4) {
        try {
            // Check multiple points around the player
            for (const point of this.collisionCheckPoints) {
                const checkX = playerX + point.x;
                const checkZ = playerZ + point.z;
                
                // Get terrain height at this point
                const terrainHeight = this.getCollisionHeight(checkX, checkZ);
                
                // Check if player is inside terrain
                if (playerY <= terrainHeight) {
                    // Calculate penetration depth
                    const penetrationDepth = terrainHeight - playerY;
                    
                    // If penetration is more than a small threshold, prevent movement
                    if (penetrationDepth > 0.1) {
                        return {
                            collision: true,
                            penetrationDepth,
                            terrainHeight,
                            collisionPoint: {x: checkX, y: terrainHeight, z: checkZ}
                        };
                    }
                }
                
                // Check for head collision (assuming player height is 2 units)
                const headHeight = playerY + 2.0;
                if (this.checkHeadCollision(checkX, checkZ, headHeight)) {
                    return {
                        collision: true,
                        penetrationDepth: 0,
                        terrainHeight,
                        collisionPoint: {x: checkX, y: terrainHeight, z: checkZ},
                        isHeadCollision: true
                    };
                }
            }
            
            // No collision detected
            return {
                collision: false
            };
        } catch (error) {
            console.error('Error in strict collision check:', error);
            // Return safe fallback - assume collision to prevent clipping
            return {
                collision: true,
                error: true
            };
        }
    }

    // Modify isValidMove to use strict collision checking
    isValidMove(currentX, currentZ, targetX, targetZ, playerHeight = 2.0, maxClimbHeight = 1.1) {
        // First check current position to ensure we're not already in terrain
        const currentY = this.getCollisionHeight(currentX, currentZ);
        const currentCollision = this.checkStrictCollision(currentX, currentY, currentZ);
        
        if (currentCollision.collision) {
            return {
                valid: false,
                reason: "Currently in invalid position",
                correction: currentCollision
            };
        }

        // Get height at target position
        const targetHeight = this.getCollisionHeight(targetX, targetZ);
        
        // Check if the height difference is too steep
        const heightDifference = targetHeight - currentY;
        if (heightDifference > maxClimbHeight) {
            return {
                valid: false,
                reason: `Too steep: ${heightDifference.toFixed(2)}`
            };
        }

        // Check target position with strict collision
        const targetCollision = this.checkStrictCollision(targetX, targetHeight, targetZ);
        if (targetCollision.collision) {
            return {
                valid: false,
                reason: "Collision detected at target position",
                collision: targetCollision
            };
        }

        // Check intermediate points to prevent tunneling
        const steps = 4;
        for (let i = 1; i < steps; i++) {
            const fraction = i / steps;
            const intermediateX = currentX + (targetX - currentX) * fraction;
            const intermediateZ = currentZ + (targetZ - currentZ) * fraction;
            const intermediateHeight = this.getCollisionHeight(intermediateX, intermediateZ);
            
            const intermediateCollision = this.checkStrictCollision(
                intermediateX,
                intermediateHeight,
                intermediateZ
            );
            
            if (intermediateCollision.collision) {
                return {
                    valid: false,
                    reason: "Collision detected during movement",
                    collision: intermediateCollision
                };
            }
        }

        // All checks passed - movement is valid
        return {
            valid: true,
            targetHeight: targetHeight,
            heightDifference: heightDifference
        };
    }
    
    // For debugging - visualize collision map
    visualizeCollisionMap(scene, chunkX, chunkZ) {
        if (!this.debug) return;
        
        const key = `${chunkX},${chunkZ}`;
        const collisionMap = this.collisionMaps.get(key);
        if (!collisionMap) return;
        
        // Create debug markers for a specific chunk's height map
        const debugGroup = new THREE.Group();
        
        for (let x = 0; x < CHUNK_SIZE; x++) {
            for (let z = 0; z < CHUNK_SIZE; z++) {
                const height = collisionMap[x][z];
                
                // Skip very low blocks
                if (height <= 0) continue;
                
                // Create a small marker at each collision point
                const marker = new THREE.Mesh(
                    new THREE.BoxGeometry(0.1, 0.1, 0.1),
                    new THREE.MeshBasicMaterial({color: 0xff0000})
                );
                
                // Position at world coordinates
                marker.position.set(
                    chunkX * CHUNK_SIZE + x,
                    height, 
                    chunkZ * CHUNK_SIZE + z
                );
                
                debugGroup.add(marker);
            }
        }
        
        scene.add(debugGroup);
        return debugGroup;
    }
}
